import { useState, useEffect } from 'react';
import { X, PackagePlus, Loader2, AlertCircle } from 'lucide-react';
import { ACCENT as SECTION_COLOR, WARN, BAD } from '../../lib/uiTokens';
import Dropdown from '../receptura/Dropdown';

const TYP_CFG = {
    surovina: { path: 'suroviny', katTyp: 'MAT', label: 'surovinu' },
    obal: { path: 'obaly', katTyp: 'OBA', label: 'obal' },
    spotreba: { path: 'spotrebni-zbozi', katTyp: 'SPOZ', label: 'spotřební zboží' },
};

const flatten = (nodes, depth = 0) => {
    let r = [];
    for (const n of nodes || []) {
        r.push({ id: n.id, nazev: '— '.repeat(depth) + n.nazev });
        if (n.children?.length) r = r.concat(flatten(n.children, depth + 1));
    }
    return r;
};

/**
 * Založení nové skladové položky (surovina/obal/spotřební zboží) přímo z řádku
 * dokladu. Předvyplní název, jednotku a cenu za jednotku z dokladu; po vytvoření
 * ji rodič napáruje na řádek. Cenu uloží do skladu (pokud má doklad dodavatele).
 * Používá existující endpointy /recepty/{cesta} a /recepty/{cesta}/{id}/ceny.
 */
export default function NovaSkladovaPolozkaModal({ typ, polozka, dodavatelId, dokladDatum, jednotky = [], token, cardStyle, isDark = false, onClose, onHotovo }) {
    const cfg = TYP_CFG[typ] || TYP_CFG.surovina;

    const cenaDefault = () => {
        const bez = parseFloat(polozka?.cena_celkem_bez_dph);
        const mn = parseFloat(polozka?.mnozstvi);
        if (bez > 0 && mn > 0) return String(Math.round((bez / mn) * 10000) / 10000);
        return polozka?.cena_za_jednotku ? String(polozka.cena_za_jednotku) : '';
    };

    const [nazev, setNazev] = useState(polozka?.nazev_z_dokladu || '');
    const [jednotkaId, setJednotkaId] = useState(polozka?.jednotka_id || jednotky[0]?.id || '');
    const [kategorieId, setKategorieId] = useState('');
    const [kategorie, setKategorie] = useState([]);
    const [cena, setCena] = useState(cenaDefault);
    const [dph, setDph] = useState(polozka?.dph_sazba ?? 21);
    const [ulozitCenu, setUlozitCenu] = useState(!!dodavatelId && parseFloat(cenaDefault()) > 0);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        fetch(`/api/recepty/kategorie-strom?typ=${cfg.katTyp}`)
            .then(r => (r.ok ? r.json() : []))
            .then(t => setKategorie(flatten(t)))
            .catch(() => {});
    }, [cfg.katTyp]);

    const ulozit = async () => {
        if (!nazev.trim()) { setChyba('Zadej název.'); return; }
        if (!jednotkaId) { setChyba('Vyber jednotku.'); return; }
        setUkladam(true);
        setChyba(null);
        try {
            const r = await fetch(`/api/recepty/${cfg.path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({
                    nazev: nazev.trim(),
                    jednotka_id: Number(jednotkaId),
                    kategorie_id: kategorieId ? Number(kategorieId) : null,
                }),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                throw new Error(e.message || 'Vytvoření položky selhalo');
            }
            const created = await r.json();

            // Cena do skladu (jen když má doklad dodavatele) — chyba ceny neshodí založení/napárování
            if (ulozitCenu && dodavatelId && parseFloat(cena) > 0) {
                const rc = await fetch(`/api/recepty/${cfg.path}/${created.id}/ceny`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                    body: JSON.stringify({
                        dodavatel_id: dodavatelId,
                        cena_bez_dph: parseFloat(cena),
                        dph_sazba: Number(dph),
                        cena_jednotka_id: Number(jednotkaId),
                        platnost_od: (dokladDatum || new Date().toISOString()).slice(0, 10),
                        je_primarni: true,
                        poznamka: 'Založeno z dokladu',
                    }),
                });
                if (!rc.ok) {
                    const e = await rc.json().catch(() => ({}));
                    alert('Položka vytvořena a napárována, ale cenu se nepodařilo uložit: ' + (e.message || rc.status));
                }
            }

            onHotovo(created);
        } catch (e) {
            setChyba(e.message);
            setUkladam(false);
        }
    };

    const inp = 'w-full rounded-lg px-2.5 py-2 text-sm';
    const inpStyle = { ...cardStyle, color: 'var(--text-primary)' };
    const label = (t) => <div className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>{t}</div>;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ ...cardStyle }} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2">
                        <PackagePlus size={18} style={{ color: SECTION_COLOR }} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Nová skladová položka — {cfg.label}</h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10">
                        <X size={18} style={{ color: 'var(--text-tertiary)' }} />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <div>
                        {label('Název')}
                        <input className={inp} style={inpStyle} value={nazev} onChange={e => setNazev(e.target.value)} placeholder="Název položky" autoFocus />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            {label('Jednotka')}
                            <Dropdown value={jednotkaId} onChange={v => setJednotkaId(Number(v))}
                                options={jednotky.map(j => ({ value: j.id, label: `${j.nazev} (${j.zkratka})` }))}
                                placeholder="—" isDark={isDark} accent={SECTION_COLOR} height={38} />
                        </div>
                        <div>
                            {label('Kategorie (volitelné)')}
                            <Dropdown value={kategorieId} onChange={v => setKategorieId(v || '')}
                                options={[{ value: '', label: '— bez kategorie —' }, ...kategorie.map(k => ({ value: k.id, label: k.nazev }))]}
                                placeholder="— bez kategorie —" isDark={isDark} accent={SECTION_COLOR} height={38} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            {label('Cena / jednotka bez DPH')}
                            <input type="number" step="0.0001" min="0" className={inp} style={inpStyle} value={cena} onChange={e => setCena(e.target.value)} />
                        </div>
                        <div>
                            {label('DPH')}
                            <Dropdown value={dph} onChange={v => setDph(Number(v))}
                                options={[{ value: 0, label: '0 %' }, { value: 12, label: '12 %' }, { value: 21, label: '21 %' }]}
                                isDark={isDark} accent={SECTION_COLOR} height={38} />
                        </div>
                    </div>

                    {dodavatelId ? (
                        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                            <input type="checkbox" checked={ulozitCenu} onChange={e => setUlozitCenu(e.target.checked)} style={{ accentColor: SECTION_COLOR }} />
                            Uložit cenu do skladu (dodavatel z dokladu, platnost od data dokladu)
                        </label>
                    ) : (
                        <div className="text-[12px] flex items-start gap-1.5" style={{ color: WARN }}>
                            <AlertCircle size={14} className="shrink-0 mt-0.5" /> Doklad nemá dodavatele — položka se jen vytvoří a napáruje, cena se neuloží.
                        </div>
                    )}

                    {chyba && (
                        <div className="px-3 py-2 rounded-lg flex items-start gap-2 text-[12px]" style={{ background: `${BAD}1a`, color: BAD, border: `1px solid ${BAD}4d` }}>
                            <AlertCircle size={14} className="shrink-0 mt-0.5" /> {chyba}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} disabled={ukladam} className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50" style={{ ...cardStyle, color: 'var(--text-secondary)' }}>Zrušit</button>
                    <button onClick={ulozit} disabled={ukladam} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: SECTION_COLOR }}>
                        {ukladam ? <Loader2 size={16} className="animate-spin" /> : <PackagePlus size={16} />} Vytvořit a napárovat
                    </button>
                </div>
            </div>
        </div>
    );
}
